/**
 * Cliente HTTP de la API de TurnPro.
 *
 * Todo lo que se muestra en pantalla viene de aca: el frontend no consulta la
 * base de datos ni toma decisiones de autorizacion.
 */

import { API_URL } from "./runtime-config";

export { API_URL };

export interface ApiErrorPayload {
  readonly code: string;
  readonly message: string;
  readonly details?: unknown;
}

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.name = "ApiRequestError";
    this.code = payload.code;
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${API_URL}${path}`, {
      ...init,
      headers: {
        /*
         * Solo cuando hay algo que declarar.
         *
         * Un pedido sin cuerpo que igual dice `application/json` esta
         * mintiendo, y el servidor lo rechaza antes de llegar a la ruta. Paso
         * con la cancelacion del turno, que no manda nada: quien reservo veia
         * "Body cannot be empty when content-type is set to application/json"
         * en lugar de su turno cancelado.
         */
        ...(init?.body === undefined || init.body === null
          ? {}
          : { "content-type": "application/json" }),
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new ApiRequestError(0, {
      code: "NETWORK_ERROR",
      message: "No pudimos conectarnos. Revisa tu conexion e intenta de nuevo.",
    });
  }

  if (!response.ok) {
    let payload: ApiErrorPayload = {
      code: "UNKNOWN",
      message: "Ocurrio un error inesperado.",
    };

    try {
      const body = (await response.json()) as { error?: ApiErrorPayload };
      if (body.error) payload = body.error;
    } catch {
      // Respuesta sin cuerpo JSON: se conserva el mensaje generico.
    }

    throw new ApiRequestError(response.status, payload);
  }

  return (await response.json()) as T;
}

export interface Terminology {
  readonly professional: string;
  readonly professionalPlural: string;
  readonly customer: string;
  readonly customerPlural: string;
  readonly service: string;
  readonly servicePlural: string;
  readonly appointment: string;
  readonly appointmentPlural: string;
  readonly resource: string;
  readonly resourcePlural: string;
}

export interface TenantProfile {
  readonly tenant: {
    readonly slug: string;
    readonly name: string;
    readonly businessType: string;
    readonly timezone: string;
    readonly description: string | null;
    readonly phone: string | null;
    readonly email: string | null;
    readonly address: string | null;
    readonly socialLinks: Record<string, string>;
    /** Logo, portada y color. Ver `lib/branding.ts`. */
    readonly branding: Record<string, unknown>;
    readonly terminology: Terminology;
    readonly bookingSettings: {
      readonly slotIntervalMinutes: number;
      readonly minNoticeMinutes: number;
      readonly maxAdvanceDays: number;
      readonly allowGuestBooking: boolean;
      /** Si quien reserva puede cancelar desde el enlace de su reserva. */
      readonly allowCustomerCancellation: boolean;
      readonly cancellationMinNoticeMinutes: number;
    };
    /** Que se pide para reservar y donde transferir. */
    readonly prepayment: {
      readonly mode: "none" | "deposit" | "full";
      readonly depositPercent: number;
      readonly fullPaymentDiscountPercent: number;
      /** Alias y CBU del negocio. `null` cuando no cobra por adelantado. */
      readonly instructions: string | null;
    };
    /**
     * Si el negocio da cortesias. Con eso la reserva ofrece entrar con el
     * correo para ver la propia (D-075). Opcional: un perfil viejo en cache no
     * lo trae.
     */
    readonly loyalty?: { readonly enabled: boolean };
  };
  readonly services: readonly Service[];
  readonly professionals: readonly Professional[];
  readonly businessHours: readonly BusinessHour[];
}

export interface Service {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly durationMinutes: number;
  readonly priceCents: number | null;
  readonly currency: string;
  readonly category: string | null;
  /**
   * Cuanto hay que pagar para reservar este servicio, ya calculado por la API.
   * La pantalla no rehace la cuenta: si la hiciera, podria mostrar un importe y
   * el servidor registrar otro.
   */
  readonly prepayment: ServicePrepayment;
}

/** Una forma de pagar por adelantado, con el importe ya escrito. */
export interface PrepaymentOption {
  readonly kind: "deposit" | "full";
  readonly amountCents: number;
  readonly amountLabel: string;
  readonly discountCents: number;
  readonly discountLabel: string | null;
  readonly discountPercent: number;
  readonly balanceCents: number;
  readonly balanceLabel: string | null;
}

export interface ServicePrepayment {
  /** Si hay que pagar algo para poder reservar. */
  readonly required: boolean;
  readonly listCents: number;
  readonly listLabel: string | null;
  /** Lo que descuenta la cortesia de quien reserva (D-075). `0` sin cortesia. */
  readonly rewardDiscountCents?: number;
  readonly rewardDiscountLabel?: string | null;
  /** Lo que sale el servicio con la cortesia. */
  readonly baseLabel?: string | null;
  readonly currency: string;
  readonly options: readonly PrepaymentOption[];
}

export interface Professional {
  readonly id: string;
  readonly name: string;
  readonly headline: string | null;
  readonly bio: string | null;
  readonly avatarUrl: string | null;
  readonly serviceIds: readonly string[];
}

export interface BusinessHour {
  readonly weekday: number;
  readonly startMinute: number;
  readonly endMinute: number;
}

export interface AvailabilitySlot {
  readonly start: string;
  readonly end: string;
  readonly label: string;
  readonly startMinute: number;
}

export interface AvailabilityResponse {
  readonly timezone: string;
  readonly service: { readonly id: string; readonly name: string; readonly durationMinutes: number };
  readonly professionalId: string | null;
  readonly days: readonly { readonly date: string; readonly slots: readonly AvailabilitySlot[] }[];
}

export interface CreatedAppointment {
  readonly appointment: {
    readonly id: string;
    /**
     * Llave del enlace propio de la reserva (`/reserva/:token`). Es lo unico
     * que le queda a quien reservo sin dejar correo.
     */
    readonly manageToken: string;
    /** El WhatsApp de quien reservo, armado por la API, para mandarse el enlace. */
    readonly customerWhatsapp?: string | null;
    readonly startsAt: string;
    readonly endsAt: string;
    readonly status: string;
    readonly timezone: string;
    readonly service: { readonly id: string; readonly name: string; readonly durationMinutes: number };
    readonly professional: { readonly id: string; readonly name: string | null } | null;
    /** La cortesia que se aplico al reservar (D-075). */
    readonly reward?: {
      readonly description: string;
      readonly discountLabel: string;
      readonly totalLabel: string;
    } | null;
    /** Lo que quedo registrado del pago. `null` si el negocio no cobra al reservar. */
    readonly payment: {
      readonly kind: "deposit" | "full";
      readonly status: string;
      readonly amountCents: number;
      readonly amountLabel: string;
      readonly balanceCents: number;
      readonly balanceLabel: string | null;
      readonly instructions: string | null;
    } | null;
  };
}

/* -------------------------------------------------------------------------- */
/* Catalogo de rubros                                                          */
/*                                                                            */
/* Configuracion del producto, no datos de un negocio: que vocabulario, que    */
/* servicios y que valores de agenda trae cada rubro. La fuente es             */
/* `packages/core` y viaja resuelta desde la API; escribir una copia aca la    */
/* dejaria desincronizada el dia que se agregue un rubro.                      */
/* -------------------------------------------------------------------------- */

export interface SuggestedService {
  readonly name: string;
  readonly durationMinutes: number;
  readonly description: string | null;
}

export interface BusinessProfile {
  readonly type: string;
  readonly label: string;
  readonly tagline: string;
  readonly terminology: Terminology;
  readonly suggestedServices: readonly SuggestedService[];
  /** Opcionales: una API anterior no los manda. */
  readonly suggestedProducts?: readonly { readonly name: string; readonly category: string }[];
  readonly suggestedCustomerFields?: readonly {
    readonly label: string;
    readonly type: "text" | "longtext" | "number" | "date" | "select" | "boolean";
    readonly options: readonly string[];
    readonly hint: string;
  }[];
  readonly bookingDefaults: {
    readonly slotIntervalMinutes: number;
    readonly minNoticeMinutes: number;
    readonly maxAdvanceDays: number;
  };
}

export interface BusinessProfileGroups {
  readonly groups: readonly {
    readonly group: string;
    readonly profiles: readonly BusinessProfile[];
  }[];
}

export function getBusinessProfiles(signal?: AbortSignal): Promise<BusinessProfileGroups> {
  return request<BusinessProfileGroups>("/v1/public/rubros", { signal });
}

export function getTenantProfile(slug: string, signal?: AbortSignal): Promise<TenantProfile> {
  return request<TenantProfile>(`/v1/public/tenants/${encodeURIComponent(slug)}`, { signal });
}

export function getAvailability(
  slug: string,
  params: { serviceId: string; professionalId?: string | null; date: string; days?: number },
  signal?: AbortSignal,
): Promise<AvailabilityResponse> {
  const query = new URLSearchParams({ serviceId: params.serviceId, date: params.date });
  if (params.professionalId) query.set("professionalId", params.professionalId);
  if (params.days) query.set("days", String(params.days));

  return request<AvailabilityResponse>(
    `/v1/public/tenants/${encodeURIComponent(slug)}/availability?${query.toString()}`,
    { signal },
  );
}

export interface CreateAppointmentPayload {
  readonly serviceId: string;
  readonly professionalId?: string | null;
  readonly start: string;
  readonly notes?: string;
  readonly customer: {
    readonly name: string;
    readonly email?: string;
    readonly phone?: string;
  };
  /**
   * La forma de pago elegida. Se manda QUE eligio, nunca CUANTO: el importe lo
   * calcula el servidor. Si el monto viajara desde aca, cualquiera podria
   * reservar declarando que pago cien pesos.
   */
  readonly payment?: {
    readonly kind: "deposit" | "full";
    readonly reference?: string | null;
  };
  /**
   * La cortesia que se le mostro (D-075), por su id. El descuento lo calcula el
   * servidor, y solo vale con la sesion de "Mis turnos" de esa persona.
   */
  readonly rewardId?: string;
}

export function createAppointment(
  slug: string,
  payload: CreateAppointmentPayload,
): Promise<CreatedAppointment> {
  return request<CreatedAppointment>(
    `/v1/public/tenants/${encodeURIComponent(slug)}/appointments`,
    // Con la cookie de "Mis turnos": la cortesia solo vale con ella. En
    // produccion es el mismo origen; en desarrollo la API esta en otro puerto.
    { method: "POST", body: JSON.stringify(payload), credentials: "include" },
  );
}

/* -------------------------------------------------------------------------- */
/* Reserva por su enlace                                                      */
/*                                                                            */
/* Lo que ve quien abre el enlace del correo. No hay sesion: el token de la    */
/* URL es la credencial, y por eso nunca se guarda ni se comparte.             */
/* -------------------------------------------------------------------------- */

export interface AppointmentCancellation {
  readonly allowed: boolean;
  readonly reason:
    | "NOT_ALLOWED"
    | "ALREADY_CANCELLED"
    | "NOT_CANCELLABLE"
    | "PAST"
    | "TOO_LATE"
    | null;
  /** Por que no se puede, escrito por la API. */
  readonly message: string | null;
  /** Hasta cuando se podia cancelar, en ISO 8601. */
  readonly deadline: string | null;
  readonly minNoticeMinutes: number;
}

/** Si se puede mover el turno desde el enlace, y con que. */
export interface AppointmentReschedule {
  readonly allowed: boolean;
  readonly reason:
    | "NOT_ALLOWED"
    | "ALREADY_CANCELLED"
    | "NOT_RESCHEDULABLE"
    | "PAST"
    | "TOO_LATE"
    | "TOO_MANY_CHANGES"
    | null;
  /** Por que no se puede, escrito por la API. */
  readonly message: string | null;
  /** Hasta cuando se puede mover, en ISO 8601. */
  readonly deadline: string | null;
  readonly minNoticeMinutes: number;
  /** Cuantos cambios le quedan a este turno. */
  readonly remaining: number;
  /** Con que servicio y persona hay que pedir la disponibilidad. */
  readonly serviceId: string;
  readonly professionalId: string | null;
}

/** El pago por adelantado, tal como lo ve quien reservo. */
export interface ReservaPayment {
  readonly kind: "deposit" | "full";
  readonly status: string;
  readonly amountLabel: string;
  readonly reference: string | null;
  readonly hasReceipt: boolean;
}

export interface PublicAppointment {
  readonly appointment: {
    readonly startsAt: string;
    readonly endsAt: string;
    readonly status: string;
    readonly cancelledBy: string | null;
    readonly notes: string | null;
    readonly service: { readonly name: string; readonly durationMinutes: number };
    readonly professional: { readonly name: string } | null;
    readonly customer: { readonly name: string };
    readonly cancellation: AppointmentCancellation;
    readonly reschedule: AppointmentReschedule;
    /**
     * Opcional porque durante un despliegue el navegador puede tener el bundle
     * nuevo y la API todavia la version anterior.
     */
    readonly payment?: ReservaPayment | null;
  };
  readonly tenant: {
    readonly slug: string;
    readonly name: string;
    readonly timezone: string;
    readonly phone: string | null;
    readonly email: string | null;
    readonly address: string | null;
    readonly branding: Record<string, unknown>;
    readonly terminology: Terminology;
    /** Cuantos dias hacia adelante se puede reservar. Lo necesita el selector. */
    readonly maxAdvanceDays: number;
  };
}

export function getAppointmentByToken(
  token: string,
  signal?: AbortSignal,
): Promise<PublicAppointment> {
  return request<PublicAppointment>(`/v1/public/appointments/${encodeURIComponent(token)}`, {
    signal,
  });
}

/**
 * Sube el comprobante de la transferencia desde el enlace de la reserva.
 *
 * Va contra el token y no contra una cuenta: el token es la credencial con la
 * que esa persona ya ve y cancela su turno, y es lo unico que tiene.
 */
export function subirComprobante(token: string, data: string): Promise<{ uploaded: boolean }> {
  return request<{ uploaded: boolean }>(
    `/v1/public/appointments/${encodeURIComponent(token)}/comprobante`,
    { method: "POST", body: JSON.stringify({ data }) },
  );
}

export function cancelAppointmentByToken(token: string): Promise<PublicAppointment> {
  return request<PublicAppointment>(
    `/v1/public/appointments/${encodeURIComponent(token)}/cancel`,
    { method: "POST" },
  );
}

/**
 * Mueve el turno a otro horario.
 *
 * Se manda solo el instante nuevo: el servicio y la persona son los mismos, y
 * el servidor los toma del turno. Si viajaran desde aca, el enlace de una
 * reserva serviria para reservar cualquier otra cosa.
 */
export function rescheduleAppointmentByToken(
  token: string,
  start: string,
): Promise<PublicAppointment> {
  return request<PublicAppointment>(
    `/v1/public/appointments/${encodeURIComponent(token)}/reschedule`,
    { method: "POST", body: JSON.stringify({ start }) },
  );
}

/* -------------------------------------------------------------------------- */
/* Mis turnos (D-073)                                                         */
/* -------------------------------------------------------------------------- */
/* Quien reservo ve todos sus turnos en un negocio, con un codigo por correo.  */
/* La sesion es una cookie HttpOnly que pone la API: este codigo no la ve. Se */
/* pide con `credentials: "include"`: en produccion la API esta en el mismo   */
/* origen y daria igual, pero en desarrollo vive en otro puerto y sin esto el */
/* navegador no guarda la cookie (asi aparecio, recorriendo la pantalla).     */
/* -------------------------------------------------------------------------- */

export interface OwnAppointment {
  readonly startsAt: string;
  readonly endsAt: string;
  /** `pending` · `confirmed` · `completed` · `cancelled` · `no_show`. */
  readonly status: string;
  readonly serviceName: string;
  readonly professionalName: string | null;
  /** La llave de su enlace (`/reserva/:token`), donde se cambia o se cancela. */
  readonly token: string;
}

export interface OwnAppointments {
  readonly email: string | null;
  readonly upcoming: readonly OwnAppointment[];
  readonly past: readonly OwnAppointment[];
}

const misTurnos = (slug: string) => `/v1/public/tenants/${encodeURIComponent(slug)}/mis-turnos`;
const conCookie = { credentials: "include" } as const;

/** Pide el codigo. Responde igual tenga o no turnos ese correo. */
export function requestOwnAppointmentsCode(
  slug: string,
  email: string,
): Promise<{ readonly flow: string; readonly expiresInMinutes: number }> {
  return request(`${misTurnos(slug)}/codigo`, { ...conCookie, method: "POST", body: JSON.stringify({ email }) });
}

export function verifyOwnAppointmentsCode(slug: string, flow: string, code: string): Promise<{ readonly ok: true }> {
  return request(`${misTurnos(slug)}/verificar`, { ...conCookie, method: "POST", body: JSON.stringify({ flow, code }) });
}

/** Los turnos de quien entro. `null` si no hay sesion (todavia no pidio el codigo, o vencio). */
export async function getOwnAppointments(slug: string, signal?: AbortSignal): Promise<OwnAppointments | null> {
  try {
    return await request<OwnAppointments>(misTurnos(slug), { ...conCookie, signal });
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 401) return null;
    throw error;
  }
}

export function leaveOwnAppointments(slug: string): Promise<{ readonly ok: true }> {
  return request(`${misTurnos(slug)}/salir`, { ...conCookie, method: "POST" });
}

/**
 * La cortesia de quien reserva, con la sesion de "Mis turnos" (D-075). Sin
 * sesion la respuesta es `identified: false`, no un error.
 */
export interface OwnReward {
  readonly identified: boolean;
  readonly email?: string;
  readonly name?: string;
  readonly reward?: { readonly id: string; readonly description: string } | null;
  /** Lo que sale cada servicio con la cortesia, seña incluida. */
  readonly services?: readonly { readonly serviceId: string; readonly prepayment: ServicePrepayment }[];
}

export function getOwnReward(slug: string, signal?: AbortSignal): Promise<OwnReward> {
  return request(`/v1/public/tenants/${encodeURIComponent(slug)}/mi-cortesia`, { ...conCookie, signal });
}
